import { Table } from "dexie";
import { CacheListener } from "./CacheListener.class";

/*
*
* Repository
* 
* This class is a generic wrapper for a IDB Stored CacheListener.
* 
* The listener function, returns a CacheListener whose value is the result of the loaded data.
* 
* This data can come from one of two sources:
*  - IndexedDB: This is the default source, and the emitted data is temporary while the actual data is being loaded.
*  - Remote source: This is the actual source of truth, normally is from a remote server. It's returned by sync();
*
* */
export class Repository<T, U = string> {

    readonly #listeners: Record<string, CacheListener<T | null>> = {};
    readonly #sync: (...args: any[]) => Promise<T>;
    readonly #db: Table<T>;

    constructor ({ sync, db }: { sync: (...args: any[]) => Promise<T>, db: Table<T> }) {
        this.#sync = sync;
        this.#db = db;
    }

    async listener (id: string) {
        if (this.#listeners[id]) return this.#listeners[id];

        const cache = await this.#db.get(id);
        const cacheListener = new CacheListener<T | null>(cache ?? null, (value?: T) => this.syncValue(id, value));

        if (cache) {
            cacheListener.sync();
        } else {
            await cacheListener.sync();
        }

        this.#listeners[id] = cacheListener;

        return cacheListener;
    }

    async syncValue (id: string, value?: T): Promise<T | null> {
        const newValue = value ?? await this.#sync(id);

        await this.#db.put(newValue);

        return newValue;
    }

    sync (id: string, value?: T) {
        return this.#listeners[id]?.sync(value);
    }

    async forceSync (id: string, value?: T) {
        if (!this.#listeners[id]) await this.preload(id, value);

        return this.#listeners[id]?.sync(value);
    }

    async preload (id: string, value?: T) {
        if (!this.#listeners[id]) {
            this.#listeners[id] = new CacheListener<T | null>(value ?? null, this.syncValue.bind(this, id));
        } else {
            this.#listeners[id].sync(value);
        }

        return this.#listeners[id];
    }

    async value (id: string): Promise<T | null> {
        const listener = await this.listener(id);

        return await listener.get();
    }

    async localValue (id: string): Promise<T | null> {
        return await this.#db.get(id);
    }
}